/**
 * 店舗タグAPIクライアント（FKV3-13002）
 * バックエンドの /{accountCode}/seller-tag-types, /seller-tags, /seller-tag-maps を呼び出す
 */

import { apiDelete, apiGet, apiPost, apiPut } from './client';
import type {
  BulkSellerTagRequest,
  BulkSellerTagResponse,
  CreateSellerTagTypeRequest,
  CreateSellerTagsRequest,
  CreateSellerTagsResponse,
  ReplaceSellerTagAssignmentsRequest,
  ResolveWarehousesRequest,
  ResolveWarehousesResponse,
  SellerTagAssignmentsResponse,
  SellerTagTypesResponse,
  UpdateSellerTagTypeRequest,
  UpdateSellerTagRequest,
} from '@/lib/types/feature/seller-tag';

/** タグ種別一覧を取得する（配下のタグ名と、各タグ名の付与店舗数込み） */
export async function getSellerTagTypes(
  accountCode: string,
  authHeaders?: Record<string, string>,
): Promise<SellerTagTypesResponse> {
  return apiGet<SellerTagTypesResponse>(`/${accountCode}/seller-tag-types`, authHeaders);
}

/**
 * タグ種別を作成する。タグ名も同時に作れる。
 * 作成後のタグ種別一覧がそのまま返る。
 */
export async function createSellerTagType(
  accountCode: string,
  request: CreateSellerTagTypeRequest,
  authHeaders?: Record<string, string>,
): Promise<SellerTagTypesResponse> {
  return apiPost<SellerTagTypesResponse>(`/${accountCode}/seller-tag-types`, request, authHeaders);
}

/** タグ種別の名称・並び順を更新する */
export async function updateSellerTagType(
  accountCode: string,
  tagTypeId: string,
  request: UpdateSellerTagTypeRequest,
  authHeaders?: Record<string, string>,
): Promise<SellerTagTypesResponse> {
  return apiPut<SellerTagTypesResponse>(
    `/${accountCode}/seller-tag-types/${tagTypeId}`,
    request,
    authHeaders,
  );
}

/**
 * タグ種別を削除する。
 * 使用中（タグ名がある・店舗に付与されている）の場合、cascade を付けないと 409 になる。
 */
export async function deleteSellerTagType(
  accountCode: string,
  tagTypeId: string,
  cascade: boolean,
  authHeaders?: Record<string, string>,
): Promise<void> {
  const query = cascade ? '?cascade=true' : '';
  return apiDelete<void>(`/${accountCode}/seller-tag-types/${tagTypeId}${query}`, authHeaders);
}

/**
 * タグ名をまとめて作成する。
 * 既存と（表記揺れを吸収した上で）一致したものはエラーにならず、skipped に既存IDが入る。
 */
export async function createSellerTags(
  accountCode: string,
  tagTypeId: string,
  request: CreateSellerTagsRequest,
  authHeaders?: Record<string, string>,
): Promise<CreateSellerTagsResponse> {
  return apiPost<CreateSellerTagsResponse>(
    `/${accountCode}/seller-tag-types/${tagTypeId}/tags`,
    request,
    authHeaders,
  );
}

/** タグ名を変更する。店舗への付与は維持される */
export async function updateSellerTag(
  accountCode: string,
  tagId: string,
  request: UpdateSellerTagRequest,
  authHeaders?: Record<string, string>,
): Promise<SellerTagTypesResponse> {
  return apiPut<SellerTagTypesResponse>(
    `/${accountCode}/seller-tags/${tagId}`,
    request,
    authHeaders,
  );
}

/** タグ名を削除する。付与されている場合、cascade を付けないと 409 になる */
export async function deleteSellerTag(
  accountCode: string,
  tagId: string,
  cascade: boolean,
  authHeaders?: Record<string, string>,
): Promise<void> {
  const query = cascade ? '?cascade=true' : '';
  return apiDelete<void>(`/${accountCode}/seller-tags/${tagId}${query}`, authHeaders);
}

/** 店舗へのタグ付与を取得する */
export async function getSellerTagAssignments(
  accountCode: string,
  authHeaders?: Record<string, string>,
): Promise<SellerTagAssignmentsResponse> {
  return apiGet<SellerTagAssignmentsResponse>(`/${accountCode}/seller-tag-maps`, authHeaders);
}

/**
 * 付与・剥奪を差分で適用する。
 * 1店舗に付くのはタグ種別ごとに1件なので、付与は置き換えとして動く。
 */
export async function bulkUpdateSellerTags(
  accountCode: string,
  request: BulkSellerTagRequest,
  authHeaders?: Record<string, string>,
): Promise<BulkSellerTagResponse> {
  return apiPost<BulkSellerTagResponse>(
    `/${accountCode}/seller-tag-maps/bulk`,
    request,
    authHeaders,
  );
}

/**
 * タグ種別単位で付与を洗い替える。
 * 「CSVに無い店舗のタグを外す」を明示的に選んだときだけ使う（差分適用と経路を分けている）。
 */
export async function replaceSellerTagAssignments(
  accountCode: string,
  tagTypeId: string,
  request: ReplaceSellerTagAssignmentsRequest,
  authHeaders?: Record<string, string>,
): Promise<BulkSellerTagResponse> {
  return apiPut<BulkSellerTagResponse>(
    `/${accountCode}/seller-tag-types/${tagTypeId}/assignments`,
    request,
    authHeaders,
  );
}

/**
 * 店舗タグで指定した対象店舗を倉庫IDへ解決する（FKV3-13004 ①）。
 * 抽出条件が選ぶのは店舗ではなく店舗付随倉庫のため、バックエンドで店舗→倉庫を引く。
 */
export async function resolveTargetWarehouses(
  accountCode: string,
  request: ResolveWarehousesRequest,
  authHeaders?: Record<string, string>,
): Promise<ResolveWarehousesResponse> {
  return apiPost<ResolveWarehousesResponse>(
    `/${accountCode}/seller-tag-maps/resolve-warehouses`,
    request,
    authHeaders,
  );
}
